import _ from "lodash";
import {
  getFiniteValuesByKey,
  toFiniteNumber,
} from "../utilities";

const meanOrNull = (values) => (values.length > 0 ? _.mean(values) : null);
const maxOrNull = (values) => (values.length > 0 ? _.max(values) : null);
const minOrNull = (values) => (values.length > 0 ? _.min(values) : null);

// Used by spot history table
export function aggregateMarks(marks) {
  // Grouping by 'instrumentId', 'termId', 'qualityAttributeValueHash', and 'marketDate'
  const groupedArray = _.groupBy(
    marks,
    (obj) =>
      _.join(
        [obj.instrumentId, obj.termId, obj.qualityAttributeValueHash, obj.marketDate],
        "|"
      )
  );

  // Creating a new array with rolled-up values
  const aggregateMarks = _.map(groupedArray, (group) => {
    const {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      marketDate,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
    } = group[0];

    const overridePrice = calculateAveragePrice(group.filter((item) => item.override));
    const price = overridePrice ?? calculateAveragePrice(group);

    const high = maxOrNull(getFiniteValuesByKey(group, "high"));
    const low = minOrNull(getFiniteValuesByKey(group, "low"));
    const open = meanOrNull(getFiniteValuesByKey(group, "open"));
    const close = meanOrNull(getFiniteValuesByKey(group, "close"));
    const markState = getLowestDenominatorMarkState(group);

    return {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      marketDate,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
      price,
      high,
      low,
      open,
      close,
      markState,
    };
  });

  return aggregateMarks;
}

// Used by forward curve table
export function aggregateMarksWithoutDate(marks) {
  // Grouping by 'instrumentId', 'termId', 'qualityAttributeValueHash'
  const groupedArray = _.groupBy(
    marks,
    (obj) => _.join([obj.instrumentId, obj.termId, obj.qualityAttributeValueHash], "|")
  );

  // Creating a new array with rolled-up values
  const aggregateMarks = _.map(groupedArray, (group) => {
    const {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
    } = group[0];

    const overridePrice = calculateAveragePrice(group.filter((item) => item.override));
    const price = overridePrice ?? calculateAveragePrice(group);

    const high = maxOrNull(getFiniteValuesByKey(group, "high"));
    const low = minOrNull(getFiniteValuesByKey(group, "low"));
    const open = meanOrNull(getFiniteValuesByKey(group, "open"));
    const close = meanOrNull(getFiniteValuesByKey(group, "close"));
    const markState = getLowestDenominatorMarkState(group);

    return {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
      price,
      high,
      low,
      open,
      close,
      markState,
    };
  });

  return aggregateMarks;
}

// Used by forward curve chart
export function aggregateMarksByDate(marks) {
  // Grouping by 'instrumentId', 'termId', 'qualityAttributeValueHash', and 'date'
  const groupedArray = _.groupBy(
    marks,
    (obj) =>
      _.join([obj.instrumentId, obj.termId, obj.qualityAttributeValueHash, obj.date], "|")
  );

  // Creating a new array with rolled-up values
  const aggregateMarks = _.map(groupedArray, (group) => {
    const {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      date,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
    } = group[0];

    const overridePrice = calculateAveragePrice(group.filter((item) => item.override));
    const price = overridePrice ?? calculateAveragePrice(group);

    const high = maxOrNull(getFiniteValuesByKey(group, "high"));
    const low = minOrNull(getFiniteValuesByKey(group, "low"));
    const open = meanOrNull(getFiniteValuesByKey(group, "open"));
    const close = meanOrNull(getFiniteValuesByKey(group, "close"));
    const markState = getLowestDenominatorMarkState(group);

    return {
      instrumentId,
      termId,
      qualityAttributeValueHash,
      date,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
      price,
      high,
      low,
      open,
      close,
      markState,
    };
  });

  return aggregateMarks;
}

// Used by spot/candlestick chart
export function aggregateMarksWithoutAttributes(marks) {
  const preAggregatedMarks = aggregateMarks(marks);

  // Grouping by 'termId' and 'marketDate'
  const groupedArray = _.groupBy(preAggregatedMarks, (obj) =>
    _.join([obj.termId, obj.marketDate], "|")
  );

  // Creating a new array with rolled-up values
  const aggregatedMarks = _.map(groupedArray, (group) => {
    const {
      termId,
      marketDate,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
    } = group[0];

    const price = meanOrNull(getFiniteValuesByKey(group, "price"));
    const high = maxOrNull(getFiniteValuesByKey(group, "high"));
    const low = minOrNull(getFiniteValuesByKey(group, "low"));
    const open = meanOrNull(getFiniteValuesByKey(group, "open"));
    const close = meanOrNull(getFiniteValuesByKey(group, "close"));
    const markState = getLowestDenominatorMarkState(group);

    return {
      termId,
      marketDate,
      termName,
      currencyId,
      marketId,
      marketName,
      priceBasisId,
      uomId,
      price,
      high,
      low,
      open,
      close,
      markState,
    };
  });

  return aggregatedMarks;
}

export function calculateAveragePrice(group) {
  const validPrices = _.map(group, (record) => toFiniteNumber(record.price)).filter(
    (price) => price !== null
  );

  if (validPrices.length > 0) {
    return _.mean(validPrices);
  }

  const validBids = _.map(group, (record) => toFiniteNumber(record.bid)).filter(
    (bid) => bid !== null
  );
  const validOffers = _.map(group, (record) => toFiniteNumber(record.offer)).filter(
    (offer) => offer !== null
  );

  if (validBids.length > 0 && validOffers.length > 0) {
    const highBid = _.max(validBids);
    const lowOffer = _.min(validOffers);
    return (highBid + lowOffer) / 2;
  }

  return null;
}

export function getLowestDenominatorMarkState(group) {
  if (group.filter((x) => x.markState === "Rejected").length > 0) {
    return "Rejected";
  }
  if (group.filter((x) => x.markState === "Submitted").length > 0) {
    return "Submitted";
  }
  if (group.filter((x) => x.markState === "Approved").length > 0) {
    return "Approved";
  }

  return undefined;
}
